export const CART_STORAGE_KEY = 'saloni_cart_v1';

/** @returns {string|null} guest | user:username | null when staff (no storefront cart) */
export function resolveCartStorageScope({ ready, isStaff, isAuthed, username }) {
  if (!ready || isStaff) return null;
  if (isAuthed && username) {
    const u = String(username).trim().toLowerCase();
    if (u) return `user:${u}`;
  }
  return 'guest';
}

export function cartStorageKey(scope) {
  if (!scope) return null;
  const safe = String(scope)
    .replace(/[^a-zA-Z0-9:_-]/g, '_')
    .slice(0, 120);
  return `saloni_cart_v2_${safe}`;
}

function normalizeStoredLines(parsed) {
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((line) => {
      const stockQty =
        line.stockQty !== undefined || line.stock_qty !== undefined
          ? normalizeStockQty(line.stockQty ?? line.stock_qty)
          : null;
      const max = lineMaxQty(stockQty);
      const qty = Math.max(1, Math.min(max > 0 ? max : 1, Number(line.qty) || 1));
      const customization = String(line.customization || '').trim();
      return {
        categorySlug: String(line.categorySlug || ''),
        productId: String(line.productId || ''),
        name: String(line.name || ''),
        price: Number(line.price) || 0,
        image: line.image ? String(line.image) : '',
        stockQty,
        customization,
        qty: max === 0 ? 1 : qty,
      };
    })
    .filter((line) => line.categorySlug && line.productId && line.price > 0 && lineMaxQty(line.stockQty) > 0);
}

/** Read cart for a scope (guest or signed-in customer). Staff should pass null scope → []. */
export function readCartFromStorage(scope = 'guest') {
  if (!scope) return [];
  try {
    const key = cartStorageKey(scope);
    if (!key) return [];
    let raw = localStorage.getItem(key);
    if (!raw && scope === 'guest') {
      raw = localStorage.getItem(CART_STORAGE_KEY);
      if (raw) {
        const migrated = normalizeStoredLines(JSON.parse(raw));
        if (migrated.length > 0) {
          writeCartToStorage(migrated, 'guest');
          localStorage.removeItem(CART_STORAGE_KEY);
        }
        return migrated;
      }
    }
    if (!raw) return [];
    return normalizeStoredLines(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function writeCartToStorage(lines, scope = 'guest') {
  const key = cartStorageKey(scope);
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(lines));
}

export function clearCartFromStorage(scope = 'guest') {
  const key = cartStorageKey(scope);
  if (key) localStorage.removeItem(key);
  if (scope === 'guest') {
    localStorage.removeItem(CART_STORAGE_KEY);
  }
}

/** Unique cart line id; includes letter when personalization is set. */
export function cartLineKey(categorySlug, productId, customization) {
  const base = `${String(categorySlug || '').trim()}::${String(productId || '').trim()}`;
  const c = String(customization || '').trim();
  return c ? `${base}#${c}` : base;
}

export function parseCartLineKey(key) {
  const raw = String(key || '');
  const hash = raw.indexOf('#');
  if (hash === -1) {
    const [categorySlug, productId] = raw.split('::');
    return { categorySlug: categorySlug || '', productId: productId || '', customization: '' };
  }
  const base = raw.slice(0, hash);
  const customization = raw.slice(hash + 1);
  const [categorySlug, productId] = base.split('::');
  return { categorySlug: categorySlug || '', productId: productId || '', customization };
}

/** null = unlimited; number = units available (0 = out of stock). */
export function normalizeStockQty(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.floor(n));
}

/** Max quantity allowed in cart for this stock (99 when unlimited). */
export function lineMaxQty(stockQty) {
  if (stockQty === null || stockQty === undefined) return 99;
  const n = Number(stockQty);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(99, Math.floor(n));
}

/**
 * @param {number|null|undefined|{ stock_qty?: *, stockQty?: * }} stockQty — pass normalizeStockQty(product.stock_qty), not the whole product
 */
export function isOutOfStock(stockQty) {
  const normalized =
    stockQty !== null && typeof stockQty === 'object'
      ? normalizeStockQty(stockQty.stock_qty ?? stockQty.stockQty)
      : normalizeStockQty(stockQty);
  return lineMaxQty(normalized) === 0;
}

export function stockLimitMessage(name, max) {
  const label = String(name || 'This item').trim() || 'This item';
  if (max <= 0) return `${label} is out of stock.`;
  if (max === 1) return `Only 1 left in stock for ${label}.`;
  return `Only ${max} left in stock for ${label}.`;
}

export function cartSubtotal(lines) {
  return lines.reduce((sum, line) => sum + line.price * line.qty, 0);
}

export function cartItemCount(lines) {
  return lines.reduce((sum, line) => sum + line.qty, 0);
}
